import React, { useEffect, useState } from "react";
import { RefreshCw, Activity } from "lucide-react";
import toast from "react-hot-toast";
import api from "../api";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/logs");
      setLogs(res.data.reverse()); // newest first
    } catch (e) {
      toast.error("Gagal load logs");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const actions = ["ALL", "LOGIN", "PROCESS", "HWID_MISMATCH", "ERROR"];

  const filtered = logs.filter((l) => {
    const matchAction = actionFilter === "ALL" || l.action === actionFilter;
    const matchSearch = !filter ||
      l.key?.toLowerCase().includes(filter.toLowerCase()) ||
      l.details?.toLowerCase().includes(filter.toLowerCase()) ||
      l.ip?.includes(filter);
    return matchAction && matchSearch;
  });

  const actionColor = (action) => {
    switch (action) {
      case "LOGIN": return "#4ade80";
      case "PROCESS": return "#6366f1";
      case "HWID_MISMATCH": return "#f87171";
      case "ERROR": return "#ef4444";
      default: return "#94a3b8";
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Activity Logs</h1>
          <p style={styles.sub}>{logs.length} total entri</p>
        </div>
        <button onClick={load} style={styles.refreshBtn} disabled={loading}>
          <RefreshCw size={15} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          placeholder="Cari key, IP, detail..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={styles.search}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {actions.map((a) => (
            <button
              key={a}
              onClick={() => setActionFilter(a)}
              style={{
                ...styles.filterBtn,
                background: actionFilter === a ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.05)",
                border: actionFilter === a ? "1px solid #6366f1" : "1px solid rgba(255,255,255,0.1)",
                color: actionFilter === a ? "#a5b4fc" : "#64748b",
              }}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Log Table */}
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ flex: 2 }}>Waktu</span>
          <span style={{ flex: 2 }}>Key</span>
          <span style={{ flex: 1 }}>Action</span>
          <span style={{ flex: 1 }}>IP</span>
          <span style={{ flex: 3 }}>Detail</span>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <Activity size={32} color="#334155" />
            <p style={{ color: "#475569", marginTop: 8 }}>Tidak ada log</p>
          </div>
        ) : (
          filtered.slice(0, 200).map((log, i) => (
            <div key={i} style={styles.row}>
              <span style={{ flex: 2, color: "#64748b", fontSize: 11 }}>
                {log.timestamp?.slice(0, 19).replace("T", " ")}
              </span>
              <span style={{ flex: 2, fontFamily: "monospace", fontSize: 11, color: "#a5b4fc" }}>
                {log.key?.slice(0, 24)}
              </span>
              <span style={{ flex: 1 }}>
                <span style={{
                  padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                  background: actionColor(log.action) + "22",
                  color: actionColor(log.action),
                }}>
                  {log.action}
                </span>
              </span>
              <span style={{ flex: 1, color: "#64748b", fontSize: 12 }}>
                {log.ip || "-"}
              </span>
              <span style={{ flex: 3, color: "#94a3b8", fontSize: 12, wordBreak: "break-all" }}>
                {log.details || "-"}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: "32px 32px 32px 252px", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { color: "#fff", fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: "#64748b", fontSize: 13, marginTop: 4 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 16px", background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#94a3b8", fontSize: 13, cursor: "pointer",
  },
  search: {
    flex: 1, maxWidth: 300, padding: "8px 14px",
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, color: "#fff", fontSize: 13, outline: "none",
  },
  filterBtn: {
    padding: "7px 12px", borderRadius: 7,
    fontSize: 11, fontWeight: 600, cursor: "pointer",
  },
  table: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 },
  tableHeader: {
    display: "flex", padding: "11px 20px",
    color: "#64748b", fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  row: {
    display: "flex", padding: "11px 20px", alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },
  empty: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "48px", color: "#64748b",
  },
};
